/**
 * Life4Earth V2 — Gantt Baselines API Adapter
 *
 * Endpoint: /api/GanttBaselines
 *
 * Provides typed fetch functions for schedule baseline snapshots:
 * lock, list, fetch detail, delete, and variance calculation.
 *
 * Task: DEV-564
 */

import { apiFetch } from "./client";

// ── API Response Types ──────────────────────────────────────

export interface BaselineSummary {
  baselineNumber: number;
  taskCount: number;
  lockedAt: string;
  lockedBy: string | null;
}

export interface BaselineTask {
  id: string;
  projectId: string;
  baselineNumber: number;
  taskId: number;
  taskName: string;
  wbs: string;
  startDate: string | null;
  endDate: string | null;
  durationDays: number | null;
  percentComplete: number;
  predecessors: string | null;
  indentLevel: number;
  isMilestone: boolean;
  isSummary: boolean;
  sortOrder: number;
  lockedAt: string;
  lockedBy: string | null;
}

export interface LockBaselineResult {
  baselineNumber: number;
  taskCount: number;
  lockedAt: string;
}

export interface TaskVariance {
  taskId: number;
  taskName: string;
  actualEnd: string | null;
  baselineEnd: string | null;
  varianceDays: number | null;
}

export interface ScheduleVariance {
  baselineNumber: number;
  tasks: TaskVariance[];
  weightedVarianceDays: number | null;
  tasksOnTime: number;
  tasksLate: number;
  tasksEarly: number;
}

// ── API Functions ───────────────────────────────────────────

/** List all baselines for a project (summary only, most recent first). */
export async function fetchBaselineSummaries(
  projectGuid: string
): Promise<BaselineSummary[]> {
  return apiFetch<BaselineSummary[]>(
    `/api/GanttBaselines?projectId=${encodeURIComponent(projectGuid)}`
  );
}

/** Fetch all task snapshots for a specific baseline. */
export async function fetchBaselineTasks(
  projectGuid: string,
  baselineNumber: number
): Promise<BaselineTask[]> {
  return apiFetch<BaselineTask[]>(
    `/api/GanttBaselines/${baselineNumber}?projectId=${encodeURIComponent(projectGuid)}`
  );
}

/** Lock the current schedule as a new baseline snapshot. */
export async function lockBaseline(
  projectGuid: string
): Promise<LockBaselineResult> {
  return apiFetch<LockBaselineResult>(
    `/api/GanttBaselines/lock?projectId=${encodeURIComponent(projectGuid)}`,
    { method: "POST" }
  );
}

/** Delete a baseline (admin only). */
export async function deleteBaseline(
  projectGuid: string,
  baselineNumber: number
): Promise<void> {
  return apiFetch<void>(
    `/api/GanttBaselines/${baselineNumber}?projectId=${encodeURIComponent(projectGuid)}`,
    { method: "DELETE" }
  );
}

/** Get schedule variance (actual vs baseline end dates). */
export async function fetchScheduleVariance(
  projectGuid: string,
  baselineNumber?: number
): Promise<ScheduleVariance> {
  const url = baselineNumber != null
    ? `/api/GanttBaselines/variance?projectId=${encodeURIComponent(projectGuid)}&baselineNumber=${baselineNumber}`
    : `/api/GanttBaselines/variance?projectId=${encodeURIComponent(projectGuid)}`;
  return apiFetch<ScheduleVariance>(url);
}
